import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { TrendingUp } from "lucide-react";

export default function Showcase() {
  const [data, setData] = useState({ items: [], total_monthly: 0, total_yearly: 0 });

  useEffect(() => {
    api.get("/showcase").then((r) => setData(r.data));
  }, []);

  const byCat = data.items.reduce((acc, it) => {
    (acc[it.category] = acc[it.category] || []).push(it);
    return acc;
  }, {});

  return (
    <main data-testid="showcase-page">
      <section className="relative">
        <div className="mds-container pt-14 pb-4 grid grid-cols-1 lg:grid-cols-12 gap-8 items-center">
          <div className="lg:col-span-7">
            <span className="mds-badge mds-badge-primary"><TrendingUp size={14} /> The breakdown</span>
            <h1 className="font-heading font-semibold text-4xl sm:text-5xl lg:text-6xl mt-4 tracking-tight leading-[1.05]">
              How I save <span style={{ color: "#6B8F73" }}>£{data.total_monthly.toFixed(0)}</span> a month.
            </h1>
            <p className="text-[#4A5D54] mt-5 max-w-xl leading-relaxed text-lg">
              Every app, every panel, every card — with the honest amount each one contributes. Nothing invented, nothing exaggerated.
            </p>
          </div>
          <div className="lg:col-span-5">
            <div className="rounded-3xl overflow-hidden border border-[#E5E3DB]">
              <img
                src="https://images.unsplash.com/photo-1626266061368-46a8f578ddd6?crop=entropy&cs=srgb&fm=jpg&ixid=M3w8NjA1NTJ8MHwxfHNlYXJjaHwxfHxjbGVhbiUyMGRlc2slMjBjYWxjdWxhdG9yJTIwZmluYW5jZXxlbnwwfHx8fDE3ODMzNDY4Nzh8MA&ixlib=rb-4.1.0&q=85"
                alt="Calculator"
                className="w-full h-64 object-cover"
              />
            </div>
          </div>
        </div>
      </section>

      <section className="mds-container mt-12 grid grid-cols-1 md:grid-cols-3 gap-6">
        {[
          { label: "Monthly", value: `£${data.total_monthly.toFixed(0)}`, sub: "across all services" },
          { label: "Yearly", value: `£${data.total_yearly.toFixed(0)}`, sub: "just from stacking" },
          { label: "Services", value: data.items.length, sub: "running in parallel" },
        ].map((s, i) => (
          <div key={i} className="mds-card" data-testid={`showcase-stat-${i}`}>
            <div className="text-xs uppercase tracking-wide text-[#7A8C84]">{s.label}</div>
            <div className="font-num font-semibold text-4xl mt-1">{s.value}</div>
            <div className="text-sm text-[#4A5D54] mt-1">{s.sub}</div>
          </div>
        ))}
      </section>

      <section className="mds-container mt-14 pb-16">
        {Object.entries(byCat).map(([cat, items]) => {
          const catTotal = items.reduce((a, b) => a + b.monthly_saving, 0);
          return (
            <div key={cat} className="mb-10">
              <div className="flex items-baseline justify-between mb-4 flex-wrap gap-2">
                <h2 className="font-heading font-semibold text-2xl sm:text-3xl">{cat}</h2>
                <div className="font-num text-[#6B8F73] font-semibold">£{catTotal.toFixed(0)} / month</div>
              </div>
              <div className="rounded-2xl overflow-hidden border border-[#E5E3DB] bg-white">
                <table className="w-full text-left" data-testid={`showcase-table-${cat.replace(/\s+/g, "-").toLowerCase()}`}>
                  <thead className="bg-[#F0EFEA]/60">
                    <tr className="text-xs uppercase tracking-wide text-[#7A8C84]">
                      <th className="px-4 sm:px-6 py-3">Service</th>
                      <th className="px-4 sm:px-6 py-3 hidden sm:table-cell">Notes</th>
                      <th className="px-4 sm:px-6 py-3 text-right">Per month</th>
                    </tr>
                  </thead>
                  <tbody>
                    {items.map((it) => (
                      <tr key={it.id} className="border-t border-[#E5E3DB]">
                        <td className="px-4 sm:px-6 py-4 font-medium">{it.service_name}</td>
                        <td className="px-4 sm:px-6 py-4 text-sm text-[#4A5D54] hidden sm:table-cell">{it.notes}</td>
                        <td className="px-4 sm:px-6 py-4 text-right font-num font-semibold">£{it.monthly_saving.toFixed(0)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          );
        })}
      </section>
    </main>
  );
}
